(ns hai.notes.create
  "Standalone note creation in the expanded editor ([[hai.notes.editor]]): the
   universal bar, the notes wall, and the mobile add sheet open it on a fresh
   note — Save creates it and closes the editor. Mount [[overlay]] once at the
   app root."
  (:require
   [hai.entity.data :as entity-data]
   [hai.entity.draft :as draft]
   [hai.notes.data :as notes-data]
   [hai.notes.editor :as editor]
   [hai.notes.form :as form]
   [hai.notes.mutations :as mutations]
   [hai.notes.recent :as recent]
   [hai.spaces.data :as spaces-data]
   [hai.ui.request :as request]
   [hai.ui.rf-sugar :refer [<s >e defevent defevent-fx defsub]]
   [hai.web-app.routing :as routing]
   [hai.web-app.viewport :as viewport]))

(def draft-id
  "The draft behind the \"Creating note\" editor."
  ::draft)

(defsub ::open? [db]
  (get db ::open? false))

;; `text` seeds the draft for a host that already asked for one — the universal
;; bar hands over whatever was typed into it.
(defevent ::open [db text]
  (-> db
      (assoc ::open? true)
      (draft/seed draft-id (form/from-note {:note/text text}))))

;; The draft goes with the editor: reopening always starts fresh.
(defevent ::close [db _]
  (-> db
      (assoc ::open? false)
      (draft/discard draft-id)))

;; A new note has no saved state to restore, so Cancel empties the draft rather
;; than putting anything back.
(defevent ::reset [db _]
  (draft/seed db draft-id (form/from-note nil)))

;; Refresh whichever note list the user is looking at, so the created note
;; shows without a manual reload — and nothing otherwise.
(defn- visible-list-refresh-fx [db]
  (case (get-in db [:routing/current-route :data :name])
    :home/view      [[:dispatch [::recent/load]]]
    :timelines/view [[:dispatch [:timeline/load-entries]]]
    :notes/view     [[:dispatch [:table/resolve notes-data/table-id]]
                     [:dispatch [::notes-data/load-facets]]]
    []))

;; The note as filed — the save's `'?result` — carries the uuid the server
;; minted, so the toast can link to it. Unlike a task, whose uuid the composer
;; makes up front, there is nothing to link to until the response lands, which
;; is why the toast is dispatched from here rather than built inline.
(defevent-fx ::added-toast
  [_ note]
  {:fx [[:dispatch [:toast/success
                    (cond-> {:text "Note added"}
                      (:note/uuid note)
                      (assoc :action
                             {:label  "View note"
                              :href   (routing/href :notes/detail {:note/uuid (:note/uuid note)})
                              :target routing/link-target}))]]]})

;; The editor stays up, Save spinning, until the note is filed — then closes;
;; an error toasts and leaves the draft where it is.
(defevent-fx ::save
  [{:keys [db]} staged]
  (when-not (request/in-flight? db ::save)
    {:db (request/mark-in-flight db ::save)
     :resolve (assoc (mutations/save-resolve {:source :web-app} staged [:note/uuid])
                     :request-id ::save
                     :on-success-fx (conj (visible-list-refresh-fx db)
                                          [:dispatch [::close]]
                                          [:dispatch [::added-toast '?result]])
                     :on-error-fx [[:dispatch [:toast/error {:text "Could not save note. Try again."}]]])}))

(defn overlay
  "The \"Creating note\" editor — the expanded modal on desktop, the
   full-screen editor on mobile. Mount once at the app root; opened via
   [[::open]]."
  []
  (let [open?   @(<s [::open?])
        mobile? @(<s [::viewport/mobile?])
        saving? (= :start @(<s [:fetch/state ::save]))
        spaces? @(<s [::spaces-data/enabled?])
        editor-props {:title          "Creating note"
                      ;; No note yet: the share controls read it, and the
                      ;; composer is on a draft of its own.
                      :note           nil
                      :draft-id       draft-id
                      :spaces?        spaces?
                      :show-hint?     (not mobile?)
                      :saving?        saving?
                      :on-save        (fn [staged] (>e [::save staged]))
                      :on-cancel      #(>e [::reset])
                      :on-open-entity entity-data/open-entity
                      :on-close       #(>e [::close])}]
    (if mobile?
      (when open?
        [editor/screen editor-props])
      [editor/dialog
       {:open?    open?
        :on-close #(>e [::close])
        :title    "Creating note"}
       (when open?
         [editor/modal-content editor-props])])))
